import { inject, onMounted, onUnmounted, ref } from 'vue'
import { logger } from '@moatmetrics/vue-logger'
import { useAuth } from '@/auth/authPlugin'

/**
 * Vue composable for using SignalR service
 * Provides reactive connection state and helper methods
 */
export function useSignalR() {
    const signalRService = inject('signalRService')
    const eventBus = inject('eventBus')
    const auth = useAuth()

    const connectionState = ref({
        isConnected: false,
        isConnecting: false,
        connectionId: null,
        state: null,
    })

    // Update connection state reactively
    const updateConnectionState = () => {
        connectionState.value = signalRService.getConnectionState()
    }

    // Set up event listeners for connection state changes
    const setupConnectionListeners = () => {
        eventBus.on('signalr-connected', updateConnectionState)
        eventBus.on('signalr-disconnected', updateConnectionState)
        eventBus.on('signalr-reconnecting', updateConnectionState)
        eventBus.on('signalr-reconnected', updateConnectionState)
        eventBus.on('signalr-connection-closed', updateConnectionState)
        eventBus.on('signalr-connection-failed', updateConnectionState)
    }

    // Clean up event listeners
    const cleanupConnectionListeners = () => {
        eventBus.off('signalr-connected', updateConnectionState)
        eventBus.off('signalr-disconnected', updateConnectionState)
        eventBus.off('signalr-reconnecting', updateConnectionState)
        eventBus.off('signalr-reconnected', updateConnectionState)
        eventBus.off('signalr-connection-closed', updateConnectionState)
        eventBus.off('signalr-connection-failed', updateConnectionState)
    }

    onMounted(async () => {
        // Initialize SignalR service if not already done
        if (!signalRService.eventBus && eventBus && logger && auth) {
            signalRService.initialize(eventBus, logger, auth)

            // Auto-connect on first use
            try {
                await signalRService.connect()
            } catch (error) {
                logger.warn('Failed to establish initial SignalR connection:', error)
            }
        }

        setupConnectionListeners()
        updateConnectionState()
    })

    onUnmounted(() => {
        cleanupConnectionListeners()
    })

    /**
     * Ensure SignalR connection is established
     */
    const ensureConnection = async () => {
        return await signalRService.ensureConnection()
    }

    /**
     * Connect to SignalR
     */
    const connect = async () => {
        return await signalRService.connect()
    }

    /**
     * Disconnect from SignalR
     */
    const disconnect = async () => {
        return await signalRService.disconnect()
    }

    /**
     * Add event listener
     */
    const on = (eventName, handler) => {
        signalRService.on(eventName, handler)
    }

    /**
     * Remove event listener
     */
    const off = (eventName, handler) => {
        signalRService.off(eventName, handler)
    }

    /**
     * Invoke server method
     */
    const invoke = async (methodName, ...args) => {
        return await signalRService.invoke(methodName, ...args)
    }

    /**
     * Listen for query completed events
     */
    const onQueryCompleted = (handler) => {
        eventBus.on('signalr-query-completed', handler)

        // Return cleanup function
        return () => {
            eventBus.off('signalr-query-completed', handler)
        }
    }

    /**
     * Listen for query error events
     */
    const onQueryError = (handler) => {
        eventBus.on('signalr-query-error', handler)

        // Return cleanup function
        return () => {
            eventBus.off('signalr-query-error', handler)
        }
    }

    return {
        // Reactive state
        connectionState,

        // Connection methods
        ensureConnection,
        connect,
        disconnect,

        // Event methods
        on,
        off,
        invoke,

        // Query-specific helpers
        onQueryCompleted,
        onQueryError,

        // Direct access to service if needed
        signalRService,
    }
}
